import { Component, EventEmitter, Injector, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractComponent,
  Card,
  CardDiscardReasonEnum,
  CardQualification,
  DISCARD_REASON_LABEL,
  HEALTH_BAND_CONTEXT,
  HEALTH_BAND_LABEL,
  LEAD_ORIGIN_TYPE_LABEL,
  QUALIFICATION_CONTEXT,
  QUALIFICATION_LABEL,
  SCORE_BAND_CONTEXT,
  TASK_OUTCOME_LABEL,
  URGENCY_CONTEXT,
  URGENCY_ICON,
  URGENCY_LABEL,
  healthReasonLabelKey,
  stagnantTooltipKey,
  taskTypeLabelKey,
} from 'lib-trend-core';

/**
 * Daily-radar block of the deal card
 * (SPEC-2026-08-crm-pipeline-deals-radar — RF-05 … RF-15).
 *
 * Presentation only: every value arrives already resolved on the deal, because the
 * board must not compute anything at read time (RNF-02). The getters below map a
 * stored value onto an i18n KEY and a Bootstrap contextual class — never onto a
 * literal string and never onto a hex colour.
 *
 * Accessibility (RNF-08, AC-04): urgency carries an icon AND a text label AND an
 * aria-label. Colour is reinforcement, never the only carrier.
 */
@Component({
  selector: 'app-card-radar',
  templateUrl: './card-radar.component.html',
  styleUrls: ['./card-radar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CardRadarComponent extends AbstractComponent {

  @Input() card!: Card;

  /**
   * Board density. Compact omits every unavailable signal, so a card that knows
   * less about its lead is genuinely shorter — the board is scanned, and rows
   * that only say "no data" are rows that push the next deal off screen.
   *
   * The detail view keeps `false`: there, the absence of a score is the answer to
   * the question the reader came with.
   */
  @Input() compact = false;

  /**
   * Attendance-panel arrangement: the quality chips share one WRAPPING row (the panel
   * is read, but at 380px a stacked list of chips costs a row per signal), and the
   * commitment slot is omitted because the panel owns its next-action block.
   */
  @Input() panel = false;

  @Output() scheduleNextAction = new EventEmitter<Card>();
  @Output() openScoreExplanation = new EventEmitter<Card>();
  /** E4 RF-17: the next-action line is the conclusion's entry point on the board card (plan §11, point 4). */
  @Output() concludeNextAction = new EventEmitter<Card>();

  constructor(injector: Injector) {
    super(injector);
  }

  /**
   * A won or lost deal has no pending commitment, so urgency, the schedule
   * call-to-action and stagnation are all meaningless on it. Without this, a deal
   * sitting in the won column read "Vencida" and offered to schedule a next
   * action — the left edge was already suppressed, the badge was not.
   */
  get isClosed(): boolean {
    return this.card?.won !== null && this.card?.won !== undefined;
  }

  /**
   * Both ages on one label, and only the ones that have elapsed
   * (owner decision 2026-08-20).
   *
   * "0d" is not a reading — every deal opened today is 0d in the funnel and 0d in its
   * stage, so the label was noise on exactly the deals that carry no age information
   * at all. Each half now appears only above zero, which means a deal created today
   * shows nothing, and one created three days ago and moved today shows just
   * "3d no funil". When neither has elapsed the whole line goes, and `hasContextLine`
   * drops the row with it rather than leaving an empty muted line.
   */
  get ageLabel(): string | null {
    const parts: Array<string> = [];

    if (this.hasElapsed(this.card?.daysInPipeline)) {
      parts.push(this.t('kanban.radar.daysInPipeline', { days: this.card.daysInPipeline }));
    }
    if (this.hasElapsed(this.card?.daysInStage)) {
      parts.push(this.t('kanban.radar.daysInStage', { days: this.card.daysInStage }));
    }

    return parts.length ? parts.join(' · ') : null;
  }

  /**
   * The board and the deal detail need OPPOSITE arrangements of the same signals, and
   * the difference is the width they get (owner decision 2026-08-20).
   *
   * The board column is 350px and is SCANNED: the signals belong on one row with two
   * fixed slots, so the card's height never depends on which signals a deal carries.
   * The detail renders this block in a ~220px sidebar and is READ: the same single row
   * there put "Agendada", "Reunião · 21 ago." and "Score indisponível" on one line and
   * cut the last one down to "Score". Stacked, each signal gets the width it needs.
   *
   * Kept as class getters rather than a second template: one arrangement per screen,
   * one source of truth for what the signals ARE.
   */
  get signalRowClass(): string {
    if (this.panel) return 'flex-wrap align-items-center gap-1';
    return this.compact
      ? 'align-items-center flex-nowrap gap-2'
      : 'flex-column align-items-start gap-1';
  }

  /** The commitment slot: elastic and truncating on the board, a plain stack in the detail. */
  get signalSlotStartClass(): string {
    if (this.panel) return 'flex-wrap align-items-center gap-1';
    return this.compact
      ? 'align-items-center gap-1 flex-grow-1 min-w-0'
      : 'flex-column align-items-start gap-1';
  }

  /** The quality slot: never shrinks on the board, stacks in the detail. */
  get signalSlotEndClass(): string {
    if (this.panel) return 'flex-wrap align-items-center gap-1';
    return this.compact
      ? 'align-items-center gap-1 flex-shrink-0'
      : 'flex-column align-items-start gap-1';
  }

  /** Same split for the muted context line — interaction and ages. */
  get contextRowClass(): string {
    return this.compact
      ? 'align-items-center gap-1'
      : 'flex-column align-items-start gap-1';
  }

  get contextTextClass(): string {
    return this.compact ? 'flex-grow-1 min-w-0' : '';
  }

  /**
   * Whether the signals row has anything to put in either slot.
   *
   * An open deal always does: it either has a next action or is offered the link to
   * create one. A closed one has no commitment and no urgency, so the row is left to
   * the quality chips — and when those are absent too, rendering the row anyway cost
   * an empty `mb-1` strip under the value line, which is the kind of stray gap that
   * makes two cards side by side look misaligned.
   */
  get hasSignalRow(): boolean {
    return !this.isClosed
      || this.hasQuality
      || !this.compact
      || this.card?.leadFlags?.outOfIcp === true;
  }

  /** Whether the muted context line has anything at all to say. */
  get hasContextLine(): boolean {
    return !!this.card?.leadOrigin?.type
      || !!this.card?.lastInteraction?.at
      || !!this.ageLabel;
  }

  /**
   * A count worth printing. Guards the type as well as the value: these arrive from
   * the aggregation as `number | null`, and `null > 0` is false but `undefined > 0`
   * is too — so the explicit `Number.isFinite` keeps a non-numeric value from ever
   * reading as an age.
   */
  private hasElapsed(value: number | null | undefined): boolean {
    return Number.isFinite(value) && (value as number) > 0;
  }

  /**
   * `status === 'OK'` is the only state that carries a number. A deal whose
   * computation failed, or that predates the feature, has no score to show and must
   * say so rather than show a zero (RNF-06, RNF-09, AC-23).
   */
  get hasQuality(): boolean {
    return this.card?.leadQuality?.status === 'OK'
      && this.card?.leadQuality?.score !== null
      && this.card?.leadQuality?.score !== undefined;
  }

  get urgency(): string | undefined {
    return this.card?.urgency;
  }

  get urgencyLabel(): string {
    return URGENCY_LABEL[this.card?.urgency] ?? 'kanban.urgency.scheduled';
  }

  get urgencyIcon(): string {
    return URGENCY_ICON[this.card?.urgency] ?? 'ri-calendar-check-line';
  }

  get urgencyContext(): string {
    return URGENCY_CONTEXT[this.card?.urgency] ?? 'secondary';
  }

  /**
   * A deal with no due date has no commitment to describe, so the tooltip names the
   * absence — the same fact the `NO_NEXT_ACTION` badge now states out loud. The guard
   * is total, which is why there is no `urgencyTooltip.NO_NEXT_ACTION` key: that state
   * is unreachable below.
   */
  get nextActionTooltip(): string {
    if (!this.card?.nextAction?.dueAt) return 'kanban.radar.noNextActionTooltip';
    return `kanban.radar.urgencyTooltip.${this.card?.urgency ?? 'SCHEDULED'}`;
  }

  get nextActionTypeLabel(): string {
    return taskTypeLabelKey(this.card?.nextAction?.type);
  }

  /**
   * The badge states that the deal is cold AND has nothing scheduled; the tooltip adds
   * who went quiet, which is what decides the next move (RF-15).
   */
  get stagnantTooltip(): string {
    return stagnantTooltipKey(this.card?.lastInteraction?.direction);
  }

  /** E3 RF-37: colour of the score badge; E1's primary while the deal was not recomputed yet. */
  get scoreBandContext(): string {
    return SCORE_BAND_CONTEXT[this.card?.leadQuality?.scoreBand] ?? 'primary';
  }

  /** E3 RF-02: the decided level, or the computed recommendation while nobody decided (plan AD-01). */
  get qualification(): CardQualification | undefined {
    return this.card?.qualificationManual?.level ?? this.card?.leadQuality?.qualification;
  }

  get isRecommendation(): boolean {
    return !this.card?.qualificationManual?.level && !!this.card?.leadQuality?.qualification;
  }

  /**
   * E3 RF-03: the recommendation sits above the decision — or above unqualified while
   * nothing was decided. Ordinal over the map's declaration order, which is the ladder.
   */
  get readyForPromotion(): boolean {
    const levels = Object.keys(QUALIFICATION_LABEL);
    const recommended = levels.indexOf(this.card?.leadQuality?.qualification ?? '');
    const decided = levels.indexOf(this.card?.qualificationManual?.level ?? 'UNQUALIFIED');
    return recommended > decided;
  }

  get qualificationLabel(): string {
    return QUALIFICATION_LABEL[this.qualification] ?? 'kanban.qualification.unqualified';
  }

  get qualificationContext(): string {
    return QUALIFICATION_CONTEXT[this.qualification] ?? 'secondary';
  }

  get healthBandLabel(): string {
    return HEALTH_BAND_LABEL[this.card?.leadQuality?.health?.band] ?? 'kanban.healthBand.healthy';
  }

  get healthContext(): string {
    return HEALTH_BAND_CONTEXT[this.card?.leadQuality?.health?.band] ?? 'secondary';
  }

  /** NEW carries no number (E3 RF-11). */
  get hasHealthScore(): boolean {
    return Number.isFinite(this.card?.leadQuality?.health?.score);
  }

  /** E3 RF-14: the criterion — or state — that most explains the reading. */
  get healthReasonTooltip(): string {
    const key = healthReasonLabelKey(this.card?.leadQuality?.health?.reason);
    return key ? this.t('kanban.radar.healthReasonTooltip', { reason: this.t(key) }) : '';
  }

  get healthAria(): string {
    const band = this.t(this.healthBandLabel);
    return this.hasHealthScore
      ? this.t('kanban.radar.healthAria', { score: this.card.leadQuality.health.score, band })
      : band;
  }

  /** E3 RF-19: shown once an attempt exists; a closed deal has no cadence to read. */
  get showCadence(): boolean {
    return !this.isClosed && (this.card?.cadence?.attempts ?? 0) > 0;
  }

  get cadenceMax(): number {
    return this.card?.cadence?.max ?? 0;
  }

  get cadenceDots(): string {
    const done = Math.min(this.card?.cadence?.attempts ?? 0, this.cadenceMax);
    return '●'.repeat(done) + '○'.repeat(Math.max(this.cadenceMax - done, 0));
  }

  get cadenceAria(): string {
    return this.t('kanban.radar.cadenceAria', { attempts: this.card?.cadence?.attempts ?? 0, max: this.cadenceMax });
  }

  /**
   * E3 RF-27: why a lost deal was lost — the company's reason by name, or the platform discard
   * translated. Board only: the detail has its own block for it.
   */
  get lossLabel(): string | null {
    if (!this.compact || this.card?.won !== false) return null;

    const key = this.card?.lossReasonKey as CardDiscardReasonEnum | undefined;
    if (key) return this.t(DISCARD_REASON_LABEL[key] ?? key);
    return this.card?.lossReason?.name ?? null;
  }

  get originTypeLabel(): string {
    return LEAD_ORIGIN_TYPE_LABEL[this.card?.leadOrigin?.type] ?? 'kanban.leadOriginType.OTHER';
  }

  get lastInteractionTypeLabel(): string {
    const type = this.card?.lastInteraction?.type;
    // A message carries no task type: the source itself is the label (RF-10).
    if (type === 'MESSAGE') return 'kanban.radar.interactionMessage';
    return taskTypeLabelKey(type);
  }

  get lastInteractionOutcomeLabel(): string {
    return TASK_OUTCOME_LABEL[this.card?.lastInteraction?.outcome] ?? 'kanban.taskOutcome.other';
  }

  onScheduleNextAction(event: MouseEvent): void {
    // The whole card is clickable; scheduling must not also open the deal.
    event.stopPropagation();
    event.preventDefault();
    this.scheduleNextAction.emit(this.card);
  }

  /** `d MMM` for a commitment with no set time (E4 RF-05); the time otherwise. */
  get nextActionFormat(): string {
    return this.card?.nextAction?.untimed === true ? 'd MMM' : 'd MMM, HH:mm';
  }

  onConcludeNextAction(event: MouseEvent): void {
    // Same reason as above: the line sits inside a clickable card.
    event.stopPropagation();
    event.preventDefault();
    this.concludeNextAction.emit(this.card);
  }

  onOpenScoreExplanation(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.openScoreExplanation.emit(this.card);
  }

}
